import { ReactNode } from 'react';
import { Card } from '@/Domain/components/Shared/Card/Card';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { cn } from '@/Domain/utils/cn';

export interface ConnectionDetail {
  label: string;
  /** String value, or a node (e.g. a spinner) while a live value is loading. */
  value: ReactNode;
  'data-testid'?: string;
}

interface IntegrationConnectionCardProps {
  /** Detail columns shown in an even grid (label on top, value below). */
  details: ConnectionDetail[];
  /** Action buttons rendered in a row; give each `flex-1` for equal width. */
  actions: ReactNode;
  /** Optional extra content rendered between the details and the actions row. */
  children?: ReactNode;
  'data-testid'?: string;
}

const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

/**
 * Grey "connection details" card for connected integration sheets: a row of
 * label/value columns over a row of action buttons (Intercom, Messenger, ...).
 */
export const IntegrationConnectionCard = ({
  details,
  actions,
  children,
  ...props
}: IntegrationConnectionCardProps) => (
  <Card className="bg-muted p-4 gap-3" data-testid={props['data-testid']}>
    {details.length > 0 && (
      <div className={cn('grid gap-3 text-xs', GRID_COLS[details.length] ?? 'grid-cols-3')}>
        {details.map((detail) => (
          <div key={detail.label}>
            <Label className="text-xs text-foreground-subtle font-normal">{detail.label}</Label>
            <p className="text-sm text-foreground" data-testid={detail['data-testid']}>
              {detail.value || '-'}
            </p>
          </div>
        ))}
      </div>
    )}
    {children}
    <div className="flex gap-2 pt-2">{actions}</div>
  </Card>
);

export default IntegrationConnectionCard;
